import React, {useCallback, useContext, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {Integer} from '@pb/kit-component-formatters';
import {Tooltip} from '@pb/kit-component-popups';
import {LocaleContext} from '@pb/kit-context-locale';

import {limitDetailsSelector} from '../../slices/limit-details.slice';
import {
    userSearchResultLimitsDataSelector,
    userSearchResultLimitsResultSelector,
} from '../../slices/user-search-result-limits.slice';
import {getMappedLimitColumnId} from '../../utils';

import {LimitDetails} from './limit-details';


import type {AppState} from 'src/store/typedef';

type Props = {
    userId: number,
    columnId: string,
    onHover: (userId: number, columnId: string, cached: boolean) => void,
};

export const TotalLimitCell = ({userId, columnId, onHover}: Props) => {
    const {isLoading, loadingError} = useSelector(userSearchResultLimitsDataSelector);
    const result = useSelector(userSearchResultLimitsResultSelector);
    const {locale} = useContext(LocaleContext);

    const mappedColumnId = useMemo(() => getMappedLimitColumnId(columnId), [columnId]);
    const limitDetails = useSelector((state: AppState) => limitDetailsSelector(state, userId, mappedColumnId));

    const loadLimitDetails = useCallback(() => {
        onHover(userId, columnId, !!limitDetails);
    }, [userId, columnId, onHover, limitDetails]);

    const totalLimitValue = useMemo(() => {
        const userLimits = result?.data[userId];

        if (!userLimits) {
            return null;
        }

        const limitItem = userLimits.find(limit => limit.columnId === mappedColumnId);

        if (!limitItem) {
            return null;
        }

        if (limitItem.unlimited) {
            return 'Unlimited';
        }

        return limitItem.value;
    }, [result, userId, mappedColumnId]);

    if (isLoading || loadingError || totalLimitValue === undefined) {
        return null;
    }

    return (
        <div style={{width: '100%'}}>
            <Tooltip
                color="light"
                anchor={({anchorRef, getAnchorProps}) => (
                    <span ref={anchorRef} {...getAnchorProps()} onMouseEnter={loadLimitDetails} style={{width: '100%', display: 'flex'}}>
                        {typeof totalLimitValue === 'number' ? Integer.format(totalLimitValue, locale) : totalLimitValue}
                    </span>
                )}>
                <LimitDetails limit={limitDetails}/>
            </Tooltip>
        </div>
    );
};
